import type { RoomOptionsContainer } from '@elara/client-core/containers/RoomOptionsContainer';
import type { RoomOptionModel } from '@elara/client-core/models/RoomOptionModel';

import type { ToolResultRowEnricher } from './types.js';

import { useApp, useTripsState } from '#states/hooks.js';
import { AppImage } from '#ui/AppImage.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

function useLoadedRoom(roomId: string | undefined): RoomOptionModel | undefined {
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;
  const { features } = useApp('features');
  const roomOptions = (features as unknown as { roomOptions?: RoomOptionsContainer }).roomOptions;
  const handle =
    roomId && tripId && roomOptions ? roomOptions.ClientRoomOption.get({ tripId, id: roomId }) : undefined;
  if (!handle?.isReady || handle.error) return undefined;
  return handle.data;
}

function formatRoomSubtitle(room: RoomOptionModel): string | undefined {
  const beds = room.bedTypes ?? [];
  const bedParts = beds
    .map((bt) => {
      const count = bt.count;
      const description = bt.description;
      if (typeof count === 'number' && count > 0 && description)
        return `${count} ${count === 1 ? description : `${description}s`}`;
      return description;
    })
    .filter((p): p is string => !!p);
  const bedsLabel = bedParts.length > 0 ? bedParts.join(', ') : undefined;
  const sleepsLabel =
    typeof room.maxOccupancy === 'number' && room.maxOccupancy > 0 ? `Sleeps ${room.maxOccupancy}` : undefined;
  return [bedsLabel, sleepsLabel].filter(Boolean).join(' · ') || undefined;
}

/**
 * Room rows prefer the loaded RoomOption's `displayName` (which in turn prefers
 * `generated.name` from the room-enrichment agent), a derived subtitle (bed types
 * and max occupancy) and the first image as a small thumbnail. The thumbnail
 * fetches from Firestore because the tool output strips images for LLM brevity.
 */
export const roomRowEnricher: ToolResultRowEnricher<RoomOptionModel> = {
  useModel: useLoadedRoom,
  resolveTitle: ({ model, item }) => model?.displayName ?? item.title,
  resolveSubtitle: ({ model, item }) => (model ? (formatRoomSubtitle(model) ?? item.subtitle) : item.subtitle),
  renderIcon: ({ model, toolIcon }) => {
    const firstUrl = model?.images?.[0]?.url;
    if (firstUrl) {
      return (
        <div className="shrink-0 w-4 h-4 aspect-square rounded-sm overflow-hidden bg-muted">
          <AppImage src={firstUrl} alt="" className="w-full h-full object-cover" />
        </div>
      );
    }
    return <MaterialIcon icon={toolIcon} size={IconSizes.xs} className="shrink-0 text-muted-foreground" />;
  },
};
